'use client';

export type RoleId = 'sme' | 'buyer' | 'investor' | 'certifier' | 'finance' | 'government' | 'admin';

export interface NavItem {
  label: string;
  href: string;
  badge?: string | null;
}

export interface RoleConfig {
  id: RoleId;
  title: string;
  profileName: string;
  accountLabel: string;
  dashboardPath: string;
  color: string;
  navGroups: { label: string; items: NavItem[] }[];
}

export const ROLE_CONFIGS: Record<RoleId, RoleConfig> = {
  sme: {
    id: 'sme',
    title: 'SME / Exporter',
    profileName: 'Nusantara Halal Foods Sdn Bhd',
    accountLabel: 'SME / Exporter Account',
    dashboardPath: '/dashboard/sme',
    color: 'var(--emerald-bright)',
    navGroups: [
      {
        label: 'My Business',
        items: [
          { label: 'Dashboard', href: '/dashboard/sme', badge: null },
          { label: 'Company Profile', href: '/dashboard/sme', badge: null },
          { label: 'Product Listings', href: '/global-halal-marketplace', badge: '3' },
          { label: 'Certification', href: '/digital-certification-registry', badge: null },
        ],
      },
      {
        label: 'Growth',
        items: [
          { label: 'Finance Matching', href: '/dashboard/sme', badge: '1' },
          { label: 'Business Matching', href: '/dashboard/sme', badge: '12' },
          { label: 'Trade Intelligence', href: '/dashboard/sme', badge: null },
        ],
      },
      {
        label: 'Account',
        items: [
          { label: 'Reports', href: '/dashboard/sme', badge: null },
          { label: 'Settings', href: '/dashboard/sme', badge: null },
        ],
      },
    ],
  },
  buyer: {
    id: 'buyer',
    title: 'Buyer / Trade Partner',
    profileName: 'Gulf Retail Group',
    accountLabel: 'Buyer / Trade Partner Account',
    dashboardPath: '/dashboard/buyer',
    color: 'var(--info)',
    navGroups: [
      {
        label: 'Sourcing',
        items: [
          { label: 'Dashboard', href: '/dashboard/buyer', badge: null },
          { label: 'Supplier Marketplace', href: '/global-halal-marketplace', badge: '1,248' },
          { label: 'RFQ Management', href: '/dashboard/buyer', badge: '14' },
          { label: 'Certificate Verification', href: '/digital-certification-registry', badge: null },
        ],
      },
      {
        label: 'Growth',
        items: [
          { label: 'Finance Matching', href: '/dashboard/buyer/finance-matching', badge: '8' },
          { label: 'Business Matching', href: '/dashboard/buyer/business-matching', badge: '24' },
          { label: 'Trade Intelligence', href: '/dashboard/buyer/trade-intelligence', badge: null },
        ],
      },
      {
        label: 'Account',
        items: [
          { label: 'Reports', href: '/dashboard/buyer/reports', badge: null },
          { label: 'Settings', href: '/dashboard/buyer/settings', badge: null },
        ],
      },
    ],
  },
  investor: {
    id: 'investor',
    title: 'Investor',
    profileName: 'Crescent Capital Partners',
    accountLabel: 'Investor Account',
    dashboardPath: '/dashboard/investor',
    color: 'var(--gold)',
    navGroups: [
      {
        label: 'Investments',
        items: [
          { label: 'Dashboard', href: '/dashboard/investor', badge: null },
          { label: 'Investment Exchange', href: '/halal-investment-exchange', badge: '246' },
          { label: 'Due Diligence Room', href: '/dashboard/investor', badge: '38' },
          { label: 'Certificate Verification', href: '/digital-certification-registry', badge: null },
        ],
      },
      {
        label: 'Intelligence',
        items: [
          { label: 'Trade Intelligence', href: '/dashboard/investor', badge: null },
          { label: 'Business Matching', href: '/dashboard/investor', badge: '11' },
          { label: 'Watchlist', href: '/dashboard/investor', badge: '29' },
        ],
      },
      {
        label: 'Account',
        items: [
          { label: 'Reports', href: '/dashboard/investor', badge: null },
          { label: 'Settings', href: '/dashboard/investor', badge: null },
        ],
      },
    ],
  },
  certifier: {
    id: 'certifier',
    title: 'Certification Body',
    profileName: 'Global Halal Certification Partner',
    accountLabel: 'Certification Body Account',
    dashboardPath: '/dashboard/certifier',
    color: '#A855F7',
    navGroups: [
      {
        label: 'Certification',
        items: [
          { label: 'Dashboard', href: '/dashboard/certifier', badge: null },
          { label: 'Certificate Applications', href: '/dashboard/certifier', badge: '18' },
          { label: 'Registry', href: '/digital-certification-registry', badge: null },
          { label: 'QR Verification', href: '/dashboard/certifier', badge: null },
        ],
      },
      {
        label: 'Compliance',
        items: [
          { label: 'Audit Trail', href: '/dashboard/certifier', badge: null },
          { label: 'Recognition Mapping', href: '/dashboard/certifier', badge: null },
          { label: 'Compliance Review', href: '/dashboard/certifier', badge: '7' },
        ],
      },
      {
        label: 'Account',
        items: [
          { label: 'Reports', href: '/dashboard/certifier', badge: null },
          { label: 'Settings', href: '/dashboard/certifier', badge: null },
        ],
      },
    ],
  },
  finance: {
    id: 'finance',
    title: 'Islamic Finance Partner',
    profileName: 'Amanah Islamic Finance',
    accountLabel: 'Islamic Finance Partner Account',
    dashboardPath: '/dashboard/finance',
    color: 'var(--warning)',
    navGroups: [
      {
        label: 'Financing',
        items: [
          { label: 'Dashboard', href: '/dashboard/finance', badge: null },
          { label: 'Funding Requests', href: '/dashboard/finance', badge: '64' },
          { label: 'Product Matching', href: '/dashboard/finance', badge: null },
          { label: 'SME Trust Review', href: '/dashboard/finance', badge: '28' },
        ],
      },
      {
        label: 'Operations',
        items: [
          { label: 'Takaful Requests', href: '/dashboard/finance', badge: '21' },
          { label: 'Partner Pipeline', href: '/dashboard/finance', badge: null },
        ],
      },
      {
        label: 'Account',
        items: [
          { label: 'Reports', href: '/dashboard/finance', badge: null },
          { label: 'Settings', href: '/dashboard/finance', badge: null },
        ],
      },
    ],
  },
  government: {
    id: 'government',
    title: 'Government / Industry Body',
    profileName: 'Malaysia Halal Industry Office',
    accountLabel: 'Government / Industry Body Account',
    dashboardPath: '/dashboard/government',
    color: 'var(--danger)',
    navGroups: [
      {
        label: 'Intelligence',
        items: [
          { label: 'Dashboard', href: '/dashboard/government', badge: null },
          { label: 'Trade Intelligence', href: '/dashboard/government', badge: null },
          { label: 'SME Readiness', href: '/dashboard/government', badge: null },
          { label: 'Certification Analytics', href: '/dashboard/government', badge: null },
        ],
      },
      {
        label: 'Policy',
        items: [
          { label: 'Investment Activity', href: '/dashboard/government', badge: null },
          { label: 'Country Reports', href: '/dashboard/government', badge: null },
          { label: 'Policy Reports', href: '/dashboard/government', badge: null },
          { label: 'Compliance Oversight', href: '/dashboard/government', badge: null },
        ],
      },
      {
        label: 'Account',
        items: [
          { label: 'Settings', href: '/dashboard/government', badge: null },
        ],
      },
    ],
  },
  admin: {
    id: 'admin',
    title: 'HalalCham Admin',
    profileName: 'HalalCham Platform Admin',
    accountLabel: 'HalalCham Admin Account',
    dashboardPath: '/dashboard/admin',
    color: 'var(--emerald-bright)',
    navGroups: [
      {
        label: 'Platform',
        items: [
          { label: 'Dashboard', href: '/dashboard/admin', badge: null },
          { label: 'Onboarding', href: '/dashboard/admin', badge: '84' },
          { label: 'Partner Management', href: '/dashboard/admin', badge: null },
          { label: 'Marketplace Review', href: '/global-halal-marketplace', badge: null },
        ],
      },
      {
        label: 'Registry & Risk',
        items: [
          { label: 'Certificate Registry', href: '/digital-certification-registry', badge: null },
          { label: 'Risk Monitoring', href: '/dashboard/admin', badge: '19' },
          { label: 'User Management', href: '/dashboard/admin', badge: null },
        ],
      },
      {
        label: 'System',
        items: [
          { label: 'Reports', href: '/dashboard/admin', badge: '124' },
          { label: 'Settings', href: '/dashboard/admin', badge: null },
        ],
      },
    ],
  },
};

export const ROLE_EMAIL_MAP: Record<string, RoleId> = {
  'sme@nusantarahalalfood.com': 'sme',
  'buyer@gulftrade.ae': 'buyer',
  'investor@crescentcapital.com': 'investor',
  'certifier@jakim-partner.my': 'certifier',
  'finance@amanahislamic.my': 'finance',
  'official@miti.gov.my': 'government',
  'admin@halalcham.global': 'admin',
};

export function getRoleConfig(roleId: RoleId): RoleConfig {
  return ROLE_CONFIGS[roleId];
}
